import { useState, useEffect } from "react";
import { listActiveSettings } from "../services/settings.service";

/**
 * useSettingsDropdown — Fetches active items from a Settings section
 * and returns them formatted as Ant Design Select options.
 *
 * Usage:
 *   const { options, loading } = useSettingsDropdown("designations");
 *   <Select options={options} loading={loading} />
 *
 * With filter:
 *   const { options } = useSettingsDropdown("designations", {
 *     filter: (item) => item.designationType === "rider",
 *   });
 *
 * Custom label:
 *   const { options } = useSettingsDropdown("bank-details", {
 *     labelKey: "displayName",
 *   });
 */
export default function useSettingsDropdown(section, { filter, labelKey = "name" } = {}) {
  const [options, setOptions] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!section) return;

    let cancelled = false;
    setLoading(true);

    listActiveSettings(section)
      .then((response) => {
        if (cancelled) return;

        let items = response || [];

        if (filter) {
          items = items.filter(filter);
        }

        const opts = items.map((item) => ({
          label: item[labelKey] || item.name,
          value: item._id,
        }));

        setOptions(opts);
        setData(items); // Store raw data for filtering
      })
      .catch(() => {
        if (!cancelled) {
          setOptions([]);
          setData([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [section, labelKey]); // filter intentionally excluded to avoid re-renders

  return { options, data, loading };
}
